import React, { useEffect, useMemo, useState } from "react";
import s from "../../organizationSetup/styles.scss";
import common from "../../common.scss";
import withStyles from "isomorphic-style-loader/withStyles";
import PhoneIcon from "@mui/icons-material/Phone";
import PrimaryButton from "../../../../../../toolkit/primaryButton";
import { ICharityMmsSetup } from "../../../../../../utils/firebase/mmsSetup";
import IGlobalConfig from "../../../../../../types/IGlobalConfig";
import { AlertColor } from "@mui/material";
import { useMmsOnboardingStatus } from "../hooks/useMmsOnboardingStatus";
import {
  startMmsOnboarding,
  startMmsOnboardingWithSharedTcr,
} from "../../../../../../utils/mmsOnboardingApi";
import { formatMmsApiErrorMessage } from "../../../../../../utils/mmsApiErrorMessage";
import MmsPipelineStepList from "../components/MmsPipelineStepList";
import blockerStyles from "../components/mmsPipelineStepList.scss";

const SHARED_TCR_PIPELINE_STEP_IDS = ["bw_phone_purchase", "bw_messaging_service"];

interface ConfirmationStepProps {
  mmsSetupData: ICharityMmsSetup | null;
  charityId?: string;
  config?: IGlobalConfig;
  serviceAccessToken?: string;
  onEdit: () => void;
  onExit?: () => void;
  onResume?: () => Promise<void>;
  onCancel?: () => Promise<void>;
  onChangeAreaCode?: () => void;
  onRefreshSetupData?: () => Promise<void>;
  showSnackbar?: (message: string, severity?: AlertColor) => void;
}

const ConfirmationStep: React.FC<ConfirmationStepProps> = ({
  mmsSetupData,
  charityId,
  config,
  serviceAccessToken,
  onEdit,
  onExit,
  onResume,
  onCancel,
  onChangeAreaCode,
  onRefreshSetupData,
  showSnackbar,
}) => {
  const [starting, setStarting] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [pipelineStarted, setPipelineStarted] = useState(false);
  const { status: onboardingStatus, loading: statusLoading, error: statusError, refresh } = useMmsOnboardingStatus({
    charityId,
    config,
    serviceAccessToken,
    enabled: mmsSetupData?.status === "pending",
  });

  const phoneNumberMethod = mmsSetupData?.phoneNumberMethod;
  const areaCode = mmsSetupData?.mmsSenderAreaCode;
  const phoneNumber = mmsSetupData?.bandwidth?.phoneNumber;
  const error = mmsSetupData?.error;
  const status = mmsSetupData?.status || "pending";

  const hasError = !!error;
  const isPending = status === "pending";
  const isCompleted = status === "completed";

  useEffect(() => {
    if (!isCompleted || !onExit) {
      return;
    }

    const timeout = setTimeout(() => {
      onExit();
    }, 3000);

    return () => clearTimeout(timeout);
  }, [isCompleted, onExit]);

  const canStartOnboarding = useMemo(() => {
    if (!isPending || !onboardingStatus) {
      return false;
    }
    return !onboardingStatus.hasBrand && !onboardingStatus.pendingBrandQueued;
  }, [isPending, onboardingStatus]);

  const isPipelineRunning = useMemo(() => {
    if (!isPending || !onboardingStatus) {
      return false;
    }
    const steps = onboardingStatus.steps ?? [];
    const hasActiveSteps = steps.some(
      step =>
        step.status === "in_progress" ||
        step.status === "blocked"
    );
    if (onboardingStatus.setupFailed) {
      return false;
    }
    return (
      onboardingStatus.pendingBrandQueued ||
      onboardingStatus.pendingCampaignQueued ||
      onboardingStatus.pendingMessagingQueued ||
      hasActiveSteps
    );
  }, [isPending, onboardingStatus]);

  useEffect(() => {
    if (isPipelineRunning) {
      setPipelineStarted(true);
    }
  }, [isPipelineRunning]);

  const cancelConfirmMessage = onboardingStatus?.useSharedTcr
    ? "Are you sure? This will clear your MMS setup progress. Shared Campaign Registry resources will not be deleted."
    : "Are you sure? This will deactivate the Campaign Registry campaign, delete the brand, and clear all MMS setup data. You will need to start MMS setup again.";

  const showResumeButton = useMemo(() => {
    if (!isPending || isCompleted || !onResume) {
      return false;
    }
    if (hasError) {
      return true;
    }
    if (!onboardingStatus) {
      return false;
    }
    if (onboardingStatus.setupFailed) {
      return true;
    }
    if (onboardingStatus.pipelinePaused) {
      return true;
    }
    if (isPipelineRunning) {
      return false;
    }
    if (onboardingStatus.useSharedTcr) {
      return Boolean(mmsSetupData?.mmsSenderAreaCode);
    }
    return (
      onboardingStatus.hasBrand ||
      Boolean(mmsSetupData?.mmsSenderAreaCode)
    );
  }, [
    hasError,
    isCompleted,
    isPending,
    isPipelineRunning,
    mmsSetupData?.mmsSenderAreaCode,
    onboardingStatus,
    onResume,
  ]);

  const showManageActions = useMemo(() => {
    if (isCompleted || !isPending || !onCancel) {
      return false;
    }
    if (hasError) {
      return true;
    }
    if (!onboardingStatus) {
      return false;
    }
    if (isPipelineRunning && !onboardingStatus.setupFailed) {
      return true;
    }
    if (onboardingStatus.setupFailed || onboardingStatus.pipelinePaused) {
      return true;
    }
    return showResumeButton;
  }, [
    hasError,
    isCompleted,
    isPending,
    isPipelineRunning,
    onboardingStatus,
    onCancel,
    showResumeButton,
  ]);

  const handleResume = async () => {
    if (!onResume) {
      return;
    }
    setActionLoading(true);
    setPipelineStarted(true);
    try {
      await onResume();
      await refresh();
      await onRefreshSetupData?.();
      showSnackbar?.("MMS setup resumed. Bandwidth configuration will continue shortly.", "success");
    } catch (err) {
      await refresh();
      await onRefreshSetupData?.();
      showSnackbar?.(
        formatMmsApiErrorMessage(err, "Failed to resume MMS setup."),
        "error"
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!onCancel || !window.confirm(cancelConfirmMessage)) {
      return;
    }
    setActionLoading(true);
    try {
      await onCancel();
      await refresh();
      await onRefreshSetupData?.();
      showSnackbar?.("MMS setup canceled.", "success");
    } catch (err) {
      showSnackbar?.(
        formatMmsApiErrorMessage(err, "Failed to cancel MMS setup."),
        "error"
      );
    } finally {
      setActionLoading(false);
    }
  };

  const renderManageActions = () => {
    if (!showManageActions || !onCancel) {
      return null;
    }
    const phonePurchaseFailed = Boolean(
      onChangeAreaCode &&
      onboardingStatus?.steps?.some(
        step => step.id === "bw_phone_purchase" && step.status === "failed"
      )
    );

    return (
      <div
        className="d-flex justify-content-center gap-3 flex-wrap"
        style={{ marginTop: "2rem" }}
      >
        {phonePurchaseFailed ? (
          <PrimaryButton
            variant="primary"
            onClick={onChangeAreaCode}
            disabled={actionLoading}
          >
            Change area code
          </PrimaryButton>
        ) : null}
        {showResumeButton && onResume ? (
          <PrimaryButton
            variant={phonePurchaseFailed ? "secondary" : "primary"}
            onClick={handleResume}
            disabled={actionLoading}
          >
            {actionLoading ? "Working..." : "Resume"}
          </PrimaryButton>
        ) : null}
        <PrimaryButton
          variant={showResumeButton ? "secondary" : "primary"}
          onClick={handleCancel}
          disabled={actionLoading}
        >
          Cancel
        </PrimaryButton>
        {onExit ? (
          <PrimaryButton variant="secondary" onClick={onExit} disabled={actionLoading}>
            Go to Dashboard
          </PrimaryButton>
        ) : null}
      </div>
    );
  };

  const showReadyToStart =
    isPending &&
    !statusLoading &&
    !pipelineStarted &&
    !isPipelineRunning &&
    canStartOnboarding;

  const handleStartOnboarding = async () => {
    if (!charityId || !config?.adminServiceUrl || !serviceAccessToken) {
      showSnackbar?.("Missing configuration. Please try again.", "error");
      return;
    }

    setStarting(true);
    setPipelineStarted(true);
    try {
      if (mmsSetupData?.useSharedTcr && mmsSetupData.sharedTcr?.entryId) {
        await startMmsOnboardingWithSharedTcr(
          config.adminServiceUrl,
          charityId,
          serviceAccessToken,
          mmsSetupData.sharedTcr.entryId
        );
      } else {
        await startMmsOnboarding(
          config.adminServiceUrl,
          charityId,
          serviceAccessToken
        );
      }
      await refresh();
      await onRefreshSetupData?.();
      showSnackbar?.("MMS setup started successfully.", "success");
    } catch (err) {
      setPipelineStarted(false);
      await refresh();
      await onRefreshSetupData?.();
      const message = formatMmsApiErrorMessage(
        err,
        "Failed to start MMS setup."
      );
      showSnackbar?.(message, "error");
    } finally {
      setStarting(false);
    }
  };

  if (hasError) {
    return (
      <div className={`${common.formContainer} ${s.fadeIn}`} style={{ padding: "3rem 2rem" }}>
        <div className="text-center mb-4">
          <i className="fas fa-exclamation-circle" style={{
            fontSize: "80px",
            color: "#ED4F9D",
            marginBottom: "1rem",
            display: "block"
          }} />
          <h3 style={{
            color: "#991B1B",
            fontWeight: 600,
            fontSize: "24px",
            marginBottom: "0.5rem"
          }}>
            MMS Setup Failed
          </h3>
          <p style={{
            color: "#6b7280",
            fontSize: "16px",
            fontWeight: 400,
            marginBottom: 0
          }}>
            We weren't able to complete your MMS setup
          </p>
        </div>

        <div
          style={{
            backgroundColor: "#FEF2F2",
            border: "1px solid #FECACA",
            borderRadius: "12px",
            padding: "2rem",
            maxWidth: "600px",
            margin: "0 auto"
          }}
        >
          <p style={{
            color: "#991B1B",
            fontSize: "16px",
            fontWeight: 400,
            marginBottom: 0,
            lineHeight: 1.6
          }}>
            {error.message}
          </p>
        </div>

        {renderManageActions()}
      </div>
    );
  }

  if (isPending && statusLoading && !pipelineStarted) {
    return (
      <div className={`${common.formContainer} ${s.fadeIn}`} style={{ padding: "3rem 2rem", textAlign: "center" }}>
        <i className="fas fa-spinner fa-spin" style={{ fontSize: "28px", color: "#0078D4" }} />
        <p style={{ color: "#64748B", marginTop: "1rem", marginBottom: 0 }}>
          Checking MMS onboarding status...
        </p>
      </div>
    );
  }

  if (showReadyToStart) {
    const sharedLabel = mmsSetupData?.sharedTcr?.label;

    return (
      <div className={`${common.formContainer} ${s.fadeIn}`} style={{ padding: "3rem 2rem" }}>
        <div className="text-center" style={{ marginBottom: "2rem" }}>
          <div style={{
            width: "64px",
            height: "64px",
            borderRadius: "50%",
            backgroundColor: "rgba(0, 120, 212, 0.1)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "1.25rem"
          }}>
            <PhoneIcon style={{ color: "#0078D4", fontSize: "28px" }} />
          </div>
          <h3 style={{
            color: "#0F172A",
            fontWeight: 700,
            fontSize: "24px",
            letterSpacing: "-0.02em",
            marginBottom: "0.5rem"
          }}>
            Ready to Start MMS Setup
          </h3>
          <p style={{
            color: "#64748B",
            fontSize: "15px",
            fontWeight: 400,
            lineHeight: 1.5,
            marginBottom: 0
          }}>
            {phoneNumberMethod === "newNumber"
              ? `Area code ${areaCode} is saved. Start provisioning when you're ready.`
              : `Port request for ${areaCode} is saved. Start provisioning when you're ready.`}
          </p>
          {sharedLabel && (
            <div
              style={{
                backgroundColor: "#F8FAFC",
                border: "1px solid #E2E8F0",
                borderRadius: "12px",
                padding: "1rem 1.25rem",
                maxWidth: "480px",
                margin: "1.5rem auto 0",
                textAlign: "left",
              }}
            >
              <div
                style={{
                  color: "#94A3B8",
                  fontSize: "12px",
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  marginBottom: "4px",
                }}
              >
                Shared TCR campaign
              </div>
              <div
                style={{
                  color: "#0F172A",
                  fontSize: "16px",
                  fontWeight: 600,
                  letterSpacing: "-0.01em",
                }}
              >
                {sharedLabel}
              </div>
            </div>
          )}
        </div>

        <div className="d-flex justify-content-center gap-3">
          <PrimaryButton
            variant="primary"
            onClick={handleStartOnboarding}
            disabled={starting}
          >
            {starting ? "Starting..." : "Start MMS Setup"}
          </PrimaryButton>
          <PrimaryButton variant="secondary" onClick={onEdit}>
            Edit Selection
          </PrimaryButton>
        </div>
      </div>
    );
  }

  if (isPending && statusError && !statusLoading) {
    return (
      <div className={`${common.formContainer} ${s.fadeIn}`} style={{ padding: "3rem 2rem" }}>
        <div className="text-center mb-4">
          <i className="fas fa-exclamation-circle" style={{
            fontSize: "80px",
            color: "#ED4F9D",
            marginBottom: "1rem",
            display: "block"
          }} />
          <h3 style={{
            color: "#991B1B",
            fontWeight: 600,
            fontSize: "24px",
            marginBottom: "0.5rem"
          }}>
            Unable to Load MMS Status
          </h3>
          <p style={{
            color: "#6b7280",
            fontSize: "16px",
            fontWeight: 400,
            marginBottom: 0
          }}>
            We could not check your MMS setup progress
          </p>
        </div>

        <div
          style={{
            backgroundColor: "#FEF2F2",
            border: "1px solid #FECACA",
            borderRadius: "12px",
            padding: "2rem",
            maxWidth: "600px",
            margin: "0 auto"
          }}
        >
          <p style={{
            color: "#991B1B",
            fontSize: "16px",
            fontWeight: 400,
            marginBottom: 0,
            lineHeight: 1.6
          }}>
            {statusError}
          </p>
        </div>

        <div
          className="d-flex justify-content-center gap-3"
          style={{ marginTop: "2rem" }}
        >
          <PrimaryButton variant="primary" onClick={() => refresh()}>
            Try Again
          </PrimaryButton>
          <PrimaryButton variant="secondary" onClick={onExit}>
            Go to Dashboard
          </PrimaryButton>
        </div>
      </div>
    );
  }

  if (isPending && !statusLoading && !showReadyToStart) {
    const isPaused = onboardingStatus?.pipelinePaused;
    const steps = onboardingStatus?.steps ?? [];
    const useSharedTcr = Boolean(
      onboardingStatus?.useSharedTcr || mmsSetupData?.useSharedTcr
    );
    const sharedCampaignLabel =
      mmsSetupData?.sharedTcr?.label || onboardingStatus?.sharedTcr?.label;
    const visibleSteps = useSharedTcr
      ? steps.filter(step => SHARED_TCR_PIPELINE_STEP_IDS.includes(step.id))
      : steps;
    const blockers = onboardingStatus?.blockers ?? [];
    const hasFailedSteps = steps.some(step => step.status === "failed");
    const setupFailed = Boolean(onboardingStatus?.setupFailed || hasFailedSteps);
    const setupErrorMessage =
      error?.message ||
      onboardingStatus?.setupErrorMessage ||
      blockers.find(blockerItem => blockerItem.code === "mmsSetupError")?.message ||
      (setupFailed ? "MMS setup encountered an error. Use Resume after fixing TCR, or Cancel to start over." : null);

    const estimateText = phoneNumberMethod === 'newNumber'
      ? 'Takes about 7 business days. We\'ll notify you when complete.'
      : mmsSetupData?.estimatedDays
        ? `Porting takes ${mmsSetupData.estimatedDays} business days plus ~7 days for Campaign Registry approval.`
        : 'Porting takes 5-7 business days plus ~7 days for Campaign Registry approval.';

    return (
      <div className={`${common.formContainer} ${s.fadeIn}`} style={{ padding: "3rem 2rem" }}>
        <div className="text-center" style={{ marginBottom: "2rem" }}>
          <div style={{
            width: "64px",
            height: "64px",
            borderRadius: "50%",
            backgroundColor: setupFailed
              ? "rgba(220, 38, 38, 0.1)"
              : isPaused
                ? "rgba(220, 38, 38, 0.1)"
                : "rgba(0, 120, 212, 0.1)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "1.25rem"
          }}>
            <i
              className={setupFailed ? "fas fa-exclamation-circle" : isPaused ? "fas fa-pause-circle" : "fas fa-clock"}
              style={{
                fontSize: "28px",
                color: setupFailed || isPaused ? "#dc2626" : "#0078D4"
              }}
            />
          </div>
          <h3 style={{
            color: setupFailed ? "#991B1B" : "#0F172A",
            fontWeight: 700,
            fontSize: "24px",
            letterSpacing: "-0.02em",
            marginBottom: "0.5rem"
          }}>
            {setupFailed ? "Setup Failed" : isPaused ? "Setup in Pause" : "Setup In Progress"}
          </h3>
          <p style={{
            color: "#64748B",
            fontSize: "15px",
            fontWeight: 400,
            lineHeight: 1.5,
            marginBottom: 0
          }}>
            {setupFailed
              ? "We weren't able to complete your MMS setup"
              : isPaused
                ? "Some items need your attention"
                : "Your MMS setup is being configured"}
          </p>
          {useSharedTcr && sharedCampaignLabel ? (
            <p
              style={{
                color: "#64748B",
                fontSize: "13px",
                fontWeight: 400,
                lineHeight: 1.5,
                marginBottom: 0,
                marginTop: "0.75rem",
              }}
            >
              Using shared campaign:{" "}
              <strong style={{ color: "#475569", fontWeight: 600 }}>
                {sharedCampaignLabel}
              </strong>
            </p>
          ) : null}
        </div>

        {setupFailed && setupErrorMessage && (
          <div
            style={{
              backgroundColor: "#FEF2F2",
              border: "1px solid #FECACA",
              borderRadius: "12px",
              padding: "1.5rem",
              maxWidth: "600px",
              margin: "0 auto 1.5rem",
            }}
          >
            <p style={{
              color: "#991B1B",
              fontSize: "15px",
              fontWeight: 400,
              marginBottom: 0,
              lineHeight: 1.6
            }}>
              {setupErrorMessage}
            </p>
          </div>
        )}

        {visibleSteps.length > 0 ? (
          <MmsPipelineStepList steps={visibleSteps} />
        ) : null}

        {isPaused && !setupFailed && blockers.length > 0 && (
          <div className={blockerStyles.blockers}>
            <div className={blockerStyles.blockersTitle}>Action needed</div>
            {blockers.map((blocker) => (
              <div key={blocker.code} className={blockerStyles.blockerItem}>
                {blocker.message}
              </div>
            ))}
          </div>
        )}

        {setupFailed || showManageActions ? (
          renderManageActions()
        ) : !isPaused ? (
          <div style={{
            maxWidth: "480px",
            margin: "1rem auto 0",
            display: "flex",
            alignItems: "flex-start",
            gap: "0.5rem",
            padding: "0 0.5rem"
          }}>
            <i className="fas fa-info-circle" style={{
              color: "#94A3B8",
              fontSize: "14px",
              marginTop: "3px"
            }} />
            <p style={{
              color: "#64748B",
              fontSize: "13px",
              fontWeight: 400,
              lineHeight: 1.5,
              marginBottom: 0
            }}>
              {estimateText}
            </p>
          </div>
        ) : null}
      </div>
    );
  }

  if (isCompleted) {
    const formattedPhoneNumber = (() => {
      const source = phoneNumber != null ? String(phoneNumber) : (areaCode || "");
      const digits = source.replace(/\D/g, "");
      const last10 = digits.length > 10 ? digits.slice(-10) : digits;
      if (last10.length === 10) {
        return `+1 (${last10.slice(0, 3)}) ${last10.slice(3, 6)}-${last10.slice(6)}`;
      }
      return source;
    })();

    return (
      <div className={`${common.formContainer} ${s.fadeIn}`} style={{ padding: "3rem 2rem" }}>
        <div className="text-center" style={{ marginBottom: "2rem" }}>
          <div style={{
            width: "64px",
            height: "64px",
            borderRadius: "50%",
            backgroundColor: "rgba(22, 163, 74, 0.1)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "1.25rem"
          }}>
            <i className="fas fa-check" style={{
              fontSize: "28px",
              color: "#16a34a"
            }} />
          </div>
          <h3 style={{
            color: "#0F172A",
            fontWeight: 700,
            fontSize: "24px",
            letterSpacing: "-0.02em",
            marginBottom: "0.5rem"
          }}>
            MMS Setup Complete
          </h3>
          <p style={{
            color: "#64748B",
            fontSize: "15px",
            fontWeight: 400,
            lineHeight: 1.5,
            marginBottom: 0
          }}>
            Your phone number is ready to send MMS messages
          </p>
        </div>

        <div
          style={{
            backgroundColor: "#F8FAFC",
            border: "1px solid #E2E8F0",
            borderRadius: "12px",
            padding: "1.75rem",
            maxWidth: "480px",
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            gap: "1rem"
          }}
        >
          <div style={{
            width: "44px",
            height: "44px",
            borderRadius: "10px",
            backgroundColor: "rgba(0, 120, 212, 0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0
          }}>
            <PhoneIcon style={{ color: "#0078D4", fontSize: "22px" }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              color: "#94A3B8",
              fontSize: "12px",
              fontWeight: 500,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              marginBottom: "4px"
            }}>
              {phoneNumberMethod === 'newNumber' ? 'New Phone Number' : 'Ported Phone Number'}
            </div>
            <div style={{
              color: "#0F172A",
              fontSize: "18px",
              fontWeight: 600,
              letterSpacing: "-0.01em"
            }}>
              {formattedPhoneNumber}
            </div>
          </div>
        </div>

        {onExit ? (
          <div
            className="d-flex justify-content-center"
            style={{ marginTop: "2rem" }}
          >
            <PrimaryButton variant="primary" onClick={onExit}>
              Go to Dashboard
            </PrimaryButton>
          </div>
        ) : null}
      </div>
    );
  }

  return null;
};

export default withStyles(s, common)(ConfirmationStep);
